import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from "@mui/lab";
import { CommonRepositoty } from "../../repository";
import _ from "lodash";
import moment from "moment";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";

export const OrderTrackingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [trackingNumber] = useState(location.state?.trackingNumber ?? "");
  const [trackingDetails, setTrackingDetails] = useState(null);
  const [trackingHistory, setTrackingHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getTrackingData();
  }, [trackingNumber]);

  const getTrackingData = async () => {
    if (!trackingNumber) {
      setError("Tracking number is missing.");
      return;
    }

    setLoading(true);
    setError("");

    const response = await CommonRepositoty.onTrackShipment({
      tracking_number: trackingNumber
    });

    if (response?.status_code == 1) {
      const trackingItem = _.get(response, "data.tracking[0]", null);
      setTrackingDetails(trackingItem);
      setTrackingHistory(_.get(trackingItem, "history", []));
    } else {
      setTrackingDetails(null);
      setTrackingHistory([]);
      setError(response?.message || "Tracking is not available yet.");
    }

    setLoading(false);
  };

  const statusColor = trackingDetails?.status === "Ready for pickup" ? "success" : "primary";

  const deliveredEvent = trackingHistory.find(
    (h) => h?.description?.toLowerCase() === "delivered"
  ) || trackingHistory[0];

  const DetailRow = ({ label, value, accent }) => (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          py: 2,
          gap: 2,
        }}
      >
        <Typography
          fontWeight={700}
          sx={{ minWidth: 200, flexShrink: 0 }}
        >
          {label}
        </Typography>
        <Typography color={accent ? "primary.main" : "text.secondary"}>
          {value}
        </Typography>
      </Box>
      <Divider />
    </>
  );

  return (
    <Container sx={{ mt: 4, mb: 5 }} maxWidth="md">
      <Stack spacing={3}>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <Stack spacing={2} alignItems="center">
            <CircularProgress color="inherit" />
            <Typography color="#fff" fontWeight={700}>
              Fetching tracking details...
            </Typography>
          </Stack>
        </Backdrop>

        <Box>
          <Button
            startIcon={<ArrowBackOutlinedIcon />}
            onClick={() => navigate(-1)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Back to history
          </Button>
        </Box>

        {/* Header banner */}
        <Card
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            background: "linear-gradient(135deg, #3a1459 0%, #6e31a8 45%, #f2e7ff 100%)",
            boxShadow: "0 22px 44px rgba(87, 38, 130, 0.18)",
          }}
        >
          {loading && <LinearProgress color="inherit" />}
          <CardContent sx={{ py: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" color="#fff">
              <LocalShippingOutlinedIcon fontSize="large" />
              <Box>
                <Stack direction="row" spacing={1.2} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h5" fontWeight={800} fontFamily="Source Sans Pro">
                    {trackingDetails?.status || "Shipment Tracking"}
                  </Typography>
                  {trackingDetails?.status_code && (
                    <Chip
                      label={trackingDetails.status_code}
                      color={statusColor}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  )}
                </Stack>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Tracking ID: {trackingNumber}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {error && <Alert severity="error">{error}</Alert>}

        {/* FedEx-style details table */}
        {trackingDetails && (
          <Card sx={{ borderRadius: 4, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
            <CardContent sx={{ px: 3, pt: 2, pb: "16px !important" }}>
              <Divider />

              <DetailRow label="Tracking ID" value={trackingNumber} />

              {deliveredEvent?.datetime && (
                <DetailRow
                  label={deliveredEvent.description || "Last Update"}
                  value={moment(deliveredEvent.datetime).format("dddd, M/D/YY, h:mm A")}
                  accent
                />
              )}

              <DetailRow
                label="Delivery Status"
                value={trackingDetails.status}
                accent
              />

              <DetailRow label="From" value={trackingDetails.from} />

              <DetailRow label="To" value={trackingDetails.to} />

              {trackingDetails.ship_date && (
                <DetailRow
                  label="Ship Date"
                  value={moment(trackingDetails.ship_date).format("dddd, M/D/YY")}
                  accent
                />
              )}

              <DetailRow label="Service" value={trackingDetails.service} accent />

              {!_.isEmpty(trackingDetails.special_handlings) && (
                <>
                  <Box sx={{ display: "flex", alignItems: "flex-start", py: 2, gap: 2 }}>
                    <Typography fontWeight={700} sx={{ minWidth: 200, flexShrink: 0 }}>
                      Special Handling
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {_.map(trackingDetails.special_handlings, (item, index) => (
                        <Chip
                          key={`${item}-${index}`}
                          label={item}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            color: "primary.main",
                            borderColor: "primary.main",
                          }}
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                  <Divider />
                </>
              )}

              {trackingDetails.weight && (
                <DetailRow label="Weight" value={trackingDetails.weight} />
              )}

              {trackingDetails.dimensions && (
                <DetailRow label="Dimensions" value={trackingDetails.dimensions} />
              )}

              {trackingDetails.pickup_address && (
                <DetailRow label="Pickup Address" value={trackingDetails.pickup_address} />
              )}

              {trackingDetails.hold_notice && (
                <DetailRow label="Hold Notice" value={trackingDetails.hold_notice} accent />
              )}
            </CardContent>
          </Card>
        )}

        {/* Tracking History */}
        <Card sx={{ borderRadius: 4 }}>
          <CardContent>
            <Typography
              variant="h6"
              fontFamily="Source Sans Pro"
              fontWeight={800}
              mb={2}
            >
              Shipment Status Update
            </Typography>
            <Divider />
            {trackingHistory.length === 0 ? (
              <Typography
                variant="subtitle1"
                fontWeight={500}
                sx={{ textAlign: "center", py: 4 }}
              >
                Tracking history is not available yet.
              </Typography>
            ) : (
              <Timeline position="alternate">
                {_.map(trackingHistory, (item, index) => (
                  <TimelineItem key={`${item?.datetime}-${index}`}>
                    <TimelineSeparator>
                      <TimelineDot
                        color={
                          index === 0
                            ? "success"
                            : index === trackingHistory.length - 1
                            ? "error"
                            : "primary"
                        }
                      />
                      {index !== trackingHistory.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body1" fontWeight={700}>
                        {item?.description}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item?.datetime
                          ? moment(item.datetime).format("MMM D, YYYY - h:mm A")
                          : "Date unavailable"}
                      </Typography>
                      {item?.location && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {item.location}
                        </Typography>
                      )}
                      {item?.note && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {item.note}
                        </Typography>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};
